import {
  IEventDrawer,
  IEvents,
  IStateList,
  IWorkflowDrawer,
  IWorkflowEvents,
  IWorkflowGroup,
  IWorkflowStates,
} from "us.collection/reducers/Workflow/Interfaces";
import { EntityTypeShortForm, EntityType } from "us.common/constants";
import { WorkflowDrawerActionType } from "us.collection/constants";
import {
  workflowEventInitial,
  workflowStateInitial,
  WorkflowStateStatus,
  WorkflowSequenceType,
  WorkflowSequenceCategoryId,
} from "../Constants";
import { IStateRequest } from "../Interfaces";
import { WorkflowType, URLType } from "us.collection/constants";
import update from "immutability-helper";
import _ from "lodash";

/**
 * Add/Edit workflow state initial data
 * @param workflowDrawer - Drawer details
 * @param workflowStates - Workflow state group list
 * @param stateRequest - Get state request object
 * @returns Form initial data
 */
export const getWorkflowStateInitial = (
  workflowDrawer: IWorkflowDrawer,
  workflowStates: Array<IWorkflowGroup>,
  stateRequest: IStateRequest,
  initWorkflowVersion: number
) => {
  try {
    const { stateCategoryId, actionType, stateId, stateType } =
      workflowDrawer ?? {};

    const category = getWorkflowCategoryById(workflowStates, stateCategoryId);
    const { stateList, stateCategoryName } = category ?? {};

    if (actionType != WorkflowDrawerActionType.ADD) {
      const state = getWorkflowStateById(stateList, stateId);
      return {
        ...(state ?? {}),
        ...stateRequest,
        name:
          actionType == WorkflowDrawerActionType.COPY
            ? "Copy of " + state?.name
            : state?.name,
        stateType,
        actionType,
        workflowVersion: initWorkflowVersion,
        stateGroupName: stateCategoryName,
      };
    } else {
      return {
        ...workflowStateInitial,
        ...stateRequest,
        stateType,
        actionType,
        workflowVersion: initWorkflowVersion,
        stateGroupName: stateCategoryName,
      };
    }
  } catch {
    return workflowStateInitial;
  }
};

/**
 * Add/Edit workflow category initial data
 * @param workflowDrawer - Drawer details
 * @param workflowStates - Workflow state group list
 * @returns Form initial data
 */
export const getCategoryInitial = (
  workflowDrawer: IWorkflowDrawer,
  workflowStates: Array<IWorkflowGroup>
) => {
  try {
    const { stateCategoryId, actionType, stateType } = workflowDrawer ?? {};

    if (actionType != WorkflowDrawerActionType.ADD) {
      const category = getWorkflowCategoryById(workflowStates, stateCategoryId);

      const {
        stateCategoryName,
        stateCategoryDisplayName,
        type,
        stateCategoryDescription,
      } = category ?? {};

      return {
        categoryId: stateCategoryId,
        categoryName: stateCategoryName,
        displayName: stateCategoryDisplayName,
        description: stateCategoryDescription,
        stateType: type,
      };
    } else {
      return {
        categoryId: 0,
        categoryName: "",
        displayName: "",
        description: "",
        stateType,
      };
    }
  } catch {
    return {
      categoryId: 0,
      categoryName: "",
      displayName: "",
      description: "",
      stateType: "",
    };
  }
};

/**
 * Get category details using category id
 * @param workflowStates - Workflow state group list
 * @param id - Category Id
 * @returns Category details
 */
export const getWorkflowCategoryById = (
  workflowStates: Array<IWorkflowGroup>,
  id: number
): IWorkflowGroup | undefined => {
  try {
    return (
      workflowStates &&
      workflowStates?.find((group: any) => {
        return group.stateCategoryId == id;
      })
    );
  } catch {
    return undefined;
  }
};

/**
 * Get state details using state id
 * @param stateList - Workflow state list
 * @param id - State Id
 * @returns State details
 */
export const getWorkflowStateById = (
  stateList: Array<IStateList> | undefined,
  id: number
): IStateList | undefined => {
  try {
    return stateList?.find((stateItem: any) => {
      return stateItem.id == id;
    });
  } catch {
    return undefined;
  }
};

/**
 * Add/Edit workflow event initial data
 * @param workflowEvents Event List
 * @param eventDrawer Event drawer details
 * @param stateRequest Get event request object
 * @returns Event details
 */
export const getEventInitialDetails = (
  workflowEvents: IWorkflowEvents,
  eventDrawer: IEventDrawer,
  stateRequest: IStateRequest
) => {
  try {
    if (eventDrawer.isEdit) {
      const event = getWorkflowEventById(
        workflowEvents?.data,
        eventDrawer.eventId
      );
      return {
        ...(event ?? {}),
        ...stateRequest,
      };
    } else {
      return { ...workflowEventInitial, ...stateRequest };
    }
  } catch {
    return { ...workflowEventInitial, ...stateRequest };
  }
};
/**
 * Get event details using event id
 * @param list - Workflow event list
 * @param id - Event Id
 * @returns Event details
 */
export const getWorkflowEventById = (
  list: Array<IEvents> | undefined,
  id: number
): IEvents | undefined => {
  try {
    return list?.find((event: IEvents) => {
      return event.id == id;
    });
  } catch {
    return undefined;
  }
};

/**
 * Search Events
 * @param eventList - All event list
 * @param text - Search text
 * @returns - Filtered event list
 */
export const searchEvent = (
  eventList: Array<IEvents>,
  searchFilters: any
): { caseEvents: Array<IEvents>; subCaseEvents: Array<IEvents> } => {
  const { searchText, activeEvents } = searchFilters ?? {};

  try {
    const caseEvents = _.sortBy(
      eventList?.filter(
        ({ displayName, isDomainViewActive, workflowType }: IEvents) => {
          return (
            workflowType == EntityType.CASE &&
            displayName?.toLowerCase().includes(searchText?.toLowerCase()) &&
            getStateEventStatus(isDomainViewActive, activeEvents)
          );
        }
      ),
      "sequenceId"
    );
    const subCaseEvents = _.sortBy(
      eventList?.filter(
        ({ displayName, isDomainViewActive, workflowType }: IEvents) => {
          return (
            workflowType == EntityType.SUB_CASE &&
            displayName?.toLowerCase().includes(searchText?.toLowerCase()) &&
            getStateEventStatus(isDomainViewActive, activeEvents)
          );
        }
      ),
      "sequenceId"
    );
    return { caseEvents, subCaseEvents };
  } catch {
    return { caseEvents: [], subCaseEvents: [] };
  }
};
/**
 * @function
 * Check event name duplication
 * @param eventList event list
 * @param eventName Added name
 * @returns Duplicate or not
 */
export const checkEventNameDuplication = (
  eventList: Array<IEvents>,
  eventName: string
): boolean => {
  try {
    return eventList?.some(({ name }: any) => name == eventName);
  } catch {
    return false;
  }
};

/**
 * Filter state from state list
 * @param stateList - State List
 * @param text - Search text
 * @returns - Filtered state list
 */
export const filterState = (
  stateList: Array<IStateList>,
  values: any
): Array<IStateList> => {
  try {
    const { searchText, activeStates, overriddenBy } = values ?? {};

    return stateList?.filter((state: IStateList) => {
      return (
        state.displayName?.toLowerCase().includes(searchText?.toLowerCase()) &&
        getStateEventStatus(state.activeStatus, activeStates) &&
        (overriddenBy.length > 0
          ? overriddenBy?.some(
              (level: string) =>
                level == state?.overriddenBy?.toUpperCase()?.trim()
            )
          : true)
      );
    });
  } catch {
    return [];
  }
};

/**
 * Return active status
 * @param activeStatus - status of event or state
 * @param activeStates - user selected status
 * @returns - Return boolean according to active status
 */
const getStateEventStatus = (activeStatus: boolean, activeStates: string) => {
  switch (activeStates) {
    case WorkflowStateStatus.ACTIVE:
      return activeStatus;
    case WorkflowStateStatus.INACTIVE:
      return !activeStatus;
    case WorkflowStateStatus.ALL:
      return true;
    default:
      return true;
  }
};

/**
 * Search states
 * @param list - State list
 * @param text - Search text
 * @returns - Filtered state list with group details
 */
export const searchState = (
  list: Array<IWorkflowGroup>,
  values: any
): Array<IWorkflowGroup> | undefined => {
  try {
    const { searchText, activeStates, overriddenBy } = values ?? {};

    if (
      !searchText &&
      activeStates == WorkflowStateStatus.ALL &&
      overriddenBy?.length == 0
    ) {
      return list;
    } else {
      let groups: Array<IWorkflowGroup> = [];
      list?.map((group: IWorkflowGroup) => {
        const states = filterState([...group?.stateList], values);
        if (states.length) {
          groups.push({
            ...group,
            stateList: states,
          });
        }
      });
      return groups;
    }
  } catch {
    return [];
  }
};

/**
 * @function
 * Get Category List
 * @param workflowStates Workflow group list
 * @returns Category list
 */
export const getCategoryList = (workflowStates: IWorkflowStates) => {
  try {
    const categories = workflowStates?.data.map((group: IWorkflowGroup) => {
      return {
        label: group.stateCategoryDisplayName,
        value: group.stateCategoryName,
      };
    });
    return categories.sort((a: any, b: any) => a.label.localeCompare(b.label));
  } catch {
    return [];
  }
};

/**
 * @function
 * Get access level colour
 * @param overriddenBy State access level
 * @returns Class name
 */
export const getOverriddenColor = (overriddenBy: string) => {
  try {
    switch (overriddenBy) {
      case EntityTypeShortForm.BUREAU:
        return "";
      case EntityTypeShortForm.CREDITOR_GROUP:
        return " swg-item-ov-cg";
      case EntityTypeShortForm.CREDIT:
        return " swg-item-ov-cr";
      default:
        return "";
    }
  } catch {
    return "";
  }
};

/**
 * @function
 * Check category name duplication
 * @param workflowState State list
 * @param name Added name
 * @returns Duplicate or not
 */
export const checkCategoryNameDuplication = (
  workflowState: Array<IWorkflowGroup>,
  name: string
): boolean => {
  try {
    if (
      workflowState?.find((category: IWorkflowGroup) => {
        return category.stateCategoryName == name;
      })
    ) {
      return true;
    } else {
      return false;
    }
  } catch {
    return false;
  }
};
/**
 * @function
 * Check state name duplication
 * @param workflowState State list
 * @param name Added name
 * @returns Duplicate or not
 */
export const isDuplicateStateName = (
  workflowState: Array<IWorkflowGroup>,
  name: string
): boolean => {
  try {
    let isDuplicate = false;
    workflowState?.map((group: IWorkflowGroup) => {
      if (
        group?.stateList?.find((state: IStateList) => {
          return state.name == name;
        })
      ) {
        isDuplicate = true;
      }
    });
    return isDuplicate;
  } catch {
    return false;
  }
};
/**
 * Get workflow type for adapter sps
 * @param workflowStates State list
 * @param stateCategoryId Category Id
 * @param stateId State Id
 * @param isEdit State is new or edit
 * @returns Type
 */
export const getWorkflowType = (
  workflowStates: Array<IWorkflowGroup>,
  stateCategoryId: number,
  stateId: number,
  isEdit: boolean
): string => {
  try {
    const category = getWorkflowCategoryById(workflowStates, stateCategoryId);
    const state = getWorkflowStateById(category?.stateList, stateId);
    if (isEdit) {
      return state?.workflowType == EntityType.CASE
        ? EntityTypeShortForm.CASE
        : EntityTypeShortForm.SUB_CASE;
    }
    return EntityTypeShortForm.CASE;
  } catch {
    return EntityTypeShortForm.CASE;
  }
};

/**
 * Get Workflow type for event view
 * @param workflowEvents Event List
 * @param eventDrawer Drawer details
 * @returns Type
 */
export const getEventWorkflowType = (
  workflowEvents: IWorkflowEvents,
  eventDrawer: IEventDrawer
) => {
  try {
    const event = getWorkflowEventById(
      workflowEvents?.data,
      eventDrawer.eventId
    );
    if (eventDrawer.isEdit) {
      return event?.workflowType == EntityType.CASE
        ? EntityTypeShortForm.CASE
        : EntityTypeShortForm.SUB_CASE;
    }
    return EntityTypeShortForm.CASE;
  } catch {
    return EntityTypeShortForm.CASE;
  }
};

/**
 * Get Workflow state status title
 * @param activeStatus state status
 * @param workflowType state workflow type
 * @returns translation string
 */
export const getTagStatusTitle = (
  activeStatus: boolean,
  workflowType: string,
  isEvent?: boolean
): string => {
  try {
    const name = isEvent ? "EVENT" : "STATE";
    if (activeStatus) {
      if (workflowType == WorkflowType.CASE) {
        return `US.COLLECTION.CASE:WORKFLOWS.ACTIVE_CASE_${name}`;
      } else {
        return `US.COLLECTION.CASE:WORKFLOWS.ACTIVE_SUBCASE_${name}`;
      }
    } else {
      if (workflowType == WorkflowType.CASE) {
        return `US.COLLECTION.CASE:WORKFLOWS.INACTIVE_CASE_${name}`;
      } else {
        return `US.COLLECTION.CASE:WORKFLOWS.INACTIVE_SUBCASE_${name}`;
      }
    }
  } catch {
    return "US.COLLECTION.CASE:WORKFLOWS.ACTIVE_CASE_STATE";
  }
};

/**
 * Get Workflow state status title
 * @param Id Creditor ID or creditor group ID
 * @param navigationLevel navigation collection view level
 * @returns navigation url
 */
export const getTroubleshootNavigationUrl = (
  Id: string,
  navigationLevel: string
): string => {
  try {
    switch (navigationLevel) {
      case URLType.BUREAU:
        return "/bureau/workflows-troubleshoot";
      case URLType.CREDITOR:
        return `/creditor/${Id}/workflows-troubleshoot`;
      case URLType.CREDITOR_GROUP:
        return `/creditor-group/${Id}/workflows-troubleshoot`;
      default:
        return "";
    }
  } catch {
    return "";
  }
};

/**
 * @function
 * @description Get rest of the levels after deselection
 * @param overriddenBy array of overridden states
 * @param overriddenLevel selected level
 * @returns Rest of the levels
 */
export const getOverriddenLevels = (
  overriddenBy: Array<any>,
  overriddenLevel: string
): Array<any> => {
  try {
    return overriddenBy?.filter((level: string) => level !== overriddenLevel);
  } catch {
    return overriddenBy;
  }
};

/**
 * @description - Function for handle states and categories ordering
 * @param fromIndex - source index
 * @param toIndex - target index
 * @param workflowData - State, categories or events data
 * @param type - Wether the UI is states or other
 * @param workflowGroup - group data
 */
export const updateSequence = (
  fromIndex: number,
  toIndex: number,
  workflowData: Array<any>,
  type: "states" | "other",
  workflowGroup?: IWorkflowGroup
): Array<any> => {
  try {
    if (!Array.isArray(workflowData)) return [];

    if (type == "states") {
      const { stateCategoryId, stateList } = workflowGroup ?? {};
      if (Array.isArray(stateList)) {
        // Update the state list order
        const updatedSequence = update(stateList, {
          $splice: [
            [fromIndex, 1],
            [toIndex, 0, stateList[fromIndex]],
          ],
        }).map((item: any, idx: any) => ({ ...item, sequenceId: idx + 1 }));

        // Assign the updated items to the main state list
        const updatedStates = [...workflowData]?.map((state: any) => {
          if (stateCategoryId == state.stateCategoryId) {
            return { ...state, stateList: updatedSequence };
          } else {
            return state;
          }
        });

        return updatedStates;
      } else {
        return [];
      }
    } else {
      // Update the category or event list order
      const updatedSequence = update(workflowData, {
        $splice: [
          [fromIndex, 1],
          [toIndex, 0, workflowData[fromIndex]],
        ],
      }).map((item: any, idx: any) => ({ ...item, sequenceId: idx + 1 }));

      return updatedSequence;
    }
  } catch (error) {
    return Array.isArray(workflowData) ? workflowData : [];
  }
};

/**
 * @description - Function for return formatted payload for sequence
 * @param type - type of the sequence list
 * @param updatedSequenceList - array of updated state list
 * @returns formatted array of states, events and categories
 */
export const getSequenceItemListPayload = (
  type: WorkflowSequenceType,
  updatedSequenceList: Array<any>
): Array<any> => {
  try {
    if (Array.isArray(updatedSequenceList)) {
      if (type == WorkflowSequenceType.CATEGORY) {
        return updatedSequenceList?.map(({ stateCategoryId, sequenceId }) => ({
          objectId: stateCategoryId,
          sequenceId,
        }));
      } else {
        return updatedSequenceList?.map(({ id, sequenceId }) => ({
          objectId: id,
          sequenceId,
        }));
      }
    } else {
      return [];
    }
  } catch (e) {
    return [];
  }
};

/**
 * @description - Function for check if state list sequence is changed
 * @param workflowGroup - workflow group data
 * @param workflowInitStates - initial unchanged state list
 * @returns status of sequence
 */
export const isStateListSequenceChanged = (
  workflowGroup: IWorkflowGroup,
  workflowInitStates: Array<IWorkflowGroup>
): boolean => {
  try {
    const { stateList, stateCategoryId } = workflowGroup ?? {};

    if (
      Array.isArray(workflowInitStates) &&
      Array.isArray(stateList) &&
      stateCategoryId
    ) {
      const initialStateList =
        _.find(workflowInitStates, { stateCategoryId })?.stateList ?? [];

      const initialStateSequences = initialStateList?.map(
        ({ id, sequenceId }) => ({
          objectId: id,
          sequenceId,
        })
      );
      const currentStateSequences = stateList?.map(({ id, sequenceId }) => ({
        objectId: id,
        sequenceId,
      }));

      return !_.isEqual(initialStateSequences, currentStateSequences);
    } else {
      return false;
    }
  } catch (e) {
    return false;
  }
};

/**
 * @description - Function for return selected or deselected workflows
 * @param selectedWorkflows - previously selected workflows
 * @param workflowRecord - workflow stateRecord
 * @param stateGroupId - category id
 * @param isChecked - whether the workflow is checked
 * @param type - workflow type
 * @returns Selected or deselected workflows
 */
export const getSelectedWorkflows = (
  selectedWorkflows: Array<any>,
  workflowRecord: any,
  isChecked: boolean,
  type: "state" | "event"
): Array<any> => {
  try {
    const {
      workflowGroupStateId,
      stateGroupId,
      displayName,
      stateGroupName,
      workflowType,
    } = workflowRecord ?? {};

    if (Array.isArray(selectedWorkflows)) {
      if (isChecked) {
        return [
          ...selectedWorkflows?.filter(
            (selectedItem: any) => selectedItem?.stateGroupId == stateGroupId
          ),
          {
            workflowGroupStateId,
            stateGroupId,
            displayName,
            workflowType,
            stateGroupName: type == "state" ? stateGroupName : workflowType,
          },
        ];
      } else {
        return selectedWorkflows?.filter(
          (selectedItem: any) =>
            selectedItem?.workflowGroupStateId != workflowGroupStateId
        );
      }
    } else {
      return [];
    }
  } catch (e) {
    return [];
  }
};

/**
 * @description - Function for return all selected or deselected workflows
 * @param currentStateList - current category state list
 * @param isCheckedAll - whether the all workflow are selected related to current category checked
 * @param type - workflow type
 * @returns Selected or deselected workflows
 */
export const getAllSelectedWorkflows = (
  currentStateList: Array<any>,
  isCheckedAll: boolean,
  type: "state" | "event"
): Array<any> => {
  try {
    if (Array.isArray(currentStateList)) {
      if (isCheckedAll) {
        return currentStateList?.map(
          ({
            stateGroupId,
            workflowGroupStateId,
            displayName,
            stateGroupName,
            workflowType,
          }) => ({
            stateGroupId,
            workflowGroupStateId,
            displayName,
            stateGroupName: type == "state" ? stateGroupName : workflowType,
            workflowType,
          })
        );
      } else {
        return [];
      }
    } else {
      return [];
    }
  } catch (e) {
    return [];
  }
};

/**
 * @description - Function for check if all states are selected related to the category
 * @param values - formik values
 * @param workflowGroup - workflow group object
 * @returns Boolean
 */
export const isAllWorkflowsSelected = (
  values: any,
  workflowGroup: any
): boolean => {
  try {
    const { selectedWorkflows } = values ?? {};
    return (
      workflowGroup?.stateList?.length == selectedWorkflows?.length &&
      selectedWorkflows?.length > 0 &&
      selectedWorkflows[0]?.stateGroupId == workflowGroup?.stateCategoryId
    );
  } catch (e) {
    return false;
  }
};

/**
 * @description - Function for check if state selection is enabled
 * @param values - formik values
 * @param workflowGroup - workflow group object
 * @returns Boolean
 */
export const isStateSelectionEnabled = (
  values: any,
  workflowGroup: any
): boolean => {
  try {
    const { isDraggable, selectedWorkflows } = values ?? {};
    const { stateCategoryId, stateList } = workflowGroup ?? {};
    return (
      !isDraggable &&
      selectedWorkflows?.filter(
        ({ stateGroupId }: any) => stateGroupId == stateCategoryId
      )?.length == 0 &&
      stateList?.length > 0
    );
  } catch (e) {
    return false;
  }
};

/**
 * @description - Function for check if state export visibility
 * @param values - formik values
 * @param workflowGroup - workflow group object
 * @returns Boolean
 */
export const isSelectedStatesExportVisible = (
  values: any,
  workflowGroup: any
): boolean => {
  try {
    const { isDraggable, selectedWorkflows } = values ?? {};
    const { stateCategoryId, stateList } = workflowGroup ?? {};
    return (
      !isDraggable &&
      stateList?.length > 0 &&
      selectedWorkflows?.length > 0 &&
      selectedWorkflows[0]?.stateGroupId == stateCategoryId
    );
  } catch (e) {
    return false;
  }
};

/**
 * @description - Function for check if all events are selected
 * @param selectedWorkflows - selected workflow data
 * @param workflowType - current workflow type
 * @param events - events data
 * @returns Boolean
 */
export const isAllEventWorkflowsSelected = (
  selectedWorkflows: Array<any>,
  workflowType: EntityType,
  events: Array<any>
): boolean => {
  try {
    return (
      events?.length == selectedWorkflows?.length &&
      selectedWorkflows?.length > 0 &&
      selectedWorkflows[0]?.workflowType == workflowType
    );
  } catch (e) {
    return false;
  }
};

/**
 * @description - Function for check if event selection is enabled
 * @param values - formik values
 * @param workflowType - current workflow type
 * @param events - events data
 * @returns Boolean
 */
export const isEventSelectionEnabled = (
  values: any,
  workflowType: EntityType,
  events: Array<any>
): boolean => {
  try {
    const { isDraggable, selectedWorkflows } = values ?? {};
    return (
      !isDraggable &&
      selectedWorkflows?.filter(
        (workflow: any) => workflow?.workflowType == workflowType
      )?.length == 0 &&
      events?.length > 0
    );
  } catch (e) {
    return false;
  }
};

/**
 * @description - Function for check export is visible
 * @param values - formik values
 * @param workflowType - current workflow type
 * @param events - events data
 * @returns Boolean
 */
export const isSelectedEventExportVisible = (
  values: any,
  workflowType: EntityType,
  events: Array<any>
): boolean => {
  try {
    const { isDraggable, selectedWorkflows } = values ?? {};
    return (
      !isDraggable &&
      events?.length > 0 &&
      selectedWorkflows?.length > 0 &&
      selectedWorkflows[0]?.workflowType == workflowType
    );
  } catch (e) {
    return false;
  }
};

/**
 * @description - Function for return styles when selection is enabled
 * @param selectionEnabledCategory - selection visible category
 * @param workflowType - current workflow type
 * @returns style object
 */
export const getEventSelectionItemStyle = (
  selectionEnabledCategory: any,
  workflowType: string
): object => {
  try {
    const isSelectionVisible = selectionEnabledCategory == workflowType;
    return {
      transform: isSelectionVisible ? "translateX(20px)" : "unset",
      opacity: isSelectionVisible ? 0.85 : "unset",
      transition: "all 0.3s ease",
    };
  } catch (e) {
    return {};
  }
};